import { useCallback, useEffect, useState } from "react";
import { Check, Copy, Gift, Users, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

// Formats one of our 4 outbound channels into a share intent URL.
// The `?ch=xxx` query param is what the admin analytics groups on later —
// it's why the "share via WhatsApp" bucket looks different from a plain
// "?ref=abc" that a friend just pasted into a browser.
const CHANNEL_INTENTS = {
  whatsapp: (url, freeMinutes) =>
    `https://wa.me/?text=${encodeURIComponent(
      `I've been using LinguaSub for AI-generated subtitles that preserve slang and humour. ${freeMinutes} minutes free — one time for new users: ${url}`
    )}`,
  twitter: (url, freeMinutes) =>
    `https://twitter.com/intent/tweet?text=${encodeURIComponent(
      `Been using @LinguaSub for AI subtitles that preserve slang and humour. ${freeMinutes} free minutes once for new users:`
    )}&url=${encodeURIComponent(url)}`,
};

const buildInviteUrl = (origin, code, channel) =>
  `${origin}/?ref=${encodeURIComponent(code)}${channel ? `&ch=${channel}` : ""}`;

// WhatsApp brand icon (Simple Icons SVG path). Inlined instead of pulled
// from a brand-icon library so we avoid a runtime dependency for a single
// glyph. Same for Twitter/X below.
const WhatsAppIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
  </svg>
);

const TwitterIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

// Card that shows the user's referral link + how close they are to
// unlocking the next referral discount. Rendered on the dashboard so users
// see progress without having to hunt for a "Referrals" page.
export default function ReferralCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/referrals/me");
      setData(data);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.warn("Referral load:", err?.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Base URL for the visible text box: no channel tag, since that's for
  // channel-scoped share intents (per-button URLs below). The plain link
  // is what a user might paste manually — bucketed as "direct" server-side.
  const inviteUrl = data
    ? buildInviteUrl(window.location.origin, data.code)
    : "";

  const shareVia = useCallback(async (channel) => {
    if (!data) return;
    const url = buildInviteUrl(window.location.origin, data.code, channel);
    if (channel === "copy") {
      try {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        toast.success("Referral link copied");
        setTimeout(() => setCopied(false), 2500);
      } catch {
        const ta = document.createElement("textarea");
        ta.value = url;
        document.body.appendChild(ta);
        ta.select();
        try { document.execCommand("copy"); } finally { document.body.removeChild(ta); }
        setCopied(true);
        toast.success("Referral link copied");
        setTimeout(() => setCopied(false), 2500);
      }
      return;
    }
    const intent = CHANNEL_INTENTS[channel];
    if (!intent) return;
    // `noopener` for both privacy and to avoid the popup adopting our
    // window.opener — pretty standard for outbound share intents.
    window.open(intent(url, data.free_minutes), "_blank", "noopener,noreferrer");
  }, [data]);

  if (loading) {
    return (
      <section
        data-testid="referral-card"
        className="border border-zinc-800 bg-zinc-900/40 p-6 md:p-8 flex items-center gap-3 text-zinc-500"
      >
        <Loader2 className="w-4 h-4 animate-spin" />
        <span className="text-xs font-mono">Loading your referral link…</span>
      </section>
    );
  }
  if (!data) return null;

  const target = data.progress_target;
  const progress = data.progress || 0;
  const available = data.discounts_available || 0;
  const total = data.referred_count || 0;
  const pct = Math.min(100, Math.round((progress / target) * 100));

  return (
    <section
      data-testid="referral-card"
      className="border border-zinc-800 bg-zinc-900/40 p-6 md:p-8"
    >
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-blue-400 mb-2 flex items-center gap-1.5">
            <Gift className="w-3 h-3" /> Referrals
          </div>
          <h3 className="font-heading text-xl md:text-2xl font-bold tracking-tight text-white">
            Invite {data.progress_target} friends, earn {data.discount_percent}% off your eligible purchase.
          </h3>
          <p className="text-xs text-zinc-500 mt-2 font-mono">
            New users get {data.free_minutes} free minutes once. Only you earn the discount; referred buyers pay advertised prices. No stacking.
          </p>
        </div>
        {available > 0 && (
          <div
            data-testid="referral-discount-badge"
            className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 text-[10px] font-mono uppercase tracking-widest"
          >
            <Check className="w-3 h-3" />
            {available} discount{available > 1 ? "s" : ""} ready
          </div>
        )}
      </div>

      {/* Invite link (read-only visible URL — sends with no channel tag) */}
      <div className="flex flex-col sm:flex-row gap-2 mb-3">
        <input
          type="text"
          readOnly
          value={inviteUrl}
          onFocus={(e) => e.target.select()}
          data-testid="referral-link-input"
          className="flex-1 min-w-0 bg-zinc-950 border border-zinc-800 text-zinc-300 text-xs font-mono px-3 py-2.5 focus:outline-none focus:border-blue-500 select-all"
        />
      </div>

      {/* Channel-tagged share buttons — each generates a unique invite URL
          with `?ch=<channel>` so the admin analytics can break down which
          channels convert best. */}
      <div className="grid grid-cols-3 gap-2 mb-5">
        <button
          type="button"
          onClick={() => shareVia("whatsapp")}
          data-testid="referral-share-whatsapp"
          className="inline-flex items-center justify-center gap-2 h-10 bg-[#25D366] hover:bg-[#1DA851] text-white text-[10px] font-mono uppercase tracking-widest transition-colors"
        >
          <WhatsAppIcon className="w-3.5 h-3.5" /> WhatsApp
        </button>
        <button
          type="button"
          onClick={() => shareVia("twitter")}
          data-testid="referral-share-twitter"
          className="inline-flex items-center justify-center gap-2 h-10 bg-black border border-zinc-700 hover:bg-zinc-900 text-white text-[10px] font-mono uppercase tracking-widest transition-colors"
        >
          <TwitterIcon className="w-3.5 h-3.5" /> Twitter / X
        </button>
        <button
          type="button"
          onClick={() => shareVia("copy")}
          data-testid="referral-share-copy"
          className="inline-flex items-center justify-center gap-2 h-10 border border-zinc-800 hover:border-zinc-700 hover:bg-zinc-800 text-white text-[10px] font-mono uppercase tracking-widest transition-colors"
        >
          {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
          {copied ? "Copied" : "Copy link"}
        </button>
      </div>

      {/* Progress toward next reward */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
            <Users className="w-3 h-3" /> Progress to next {data.discount_percent}% reward
          </div>
          <span
            data-testid="referral-progress-count"
            className="text-[10px] font-mono text-white"
          >
            {progress} / {target}
          </span>
        </div>
        <div className="h-2 w-full bg-zinc-800 rounded-full overflow-hidden mb-2">
          <div
            className={
              "h-full transition-all duration-500 " +
              (progress >= target ? "bg-emerald-500" : "bg-blue-500")
            }
            style={{ width: `${pct}%` }}
          />
        </div>
        <div className="text-[10px] font-mono text-zinc-600 flex items-center gap-3 flex-wrap">
          <span data-testid="referral-total-count">{total} total signed up</span>
          <span className="text-zinc-800">·</span>
          <span>{data.discounts_earned} lifetime discount{data.discounts_earned !== 1 ? "s" : ""} earned</span>
          {data.discounts_used > 0 && (
            <>
              <span className="text-zinc-800">·</span>
              <span>{data.discounts_used} redeemed</span>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
